import React, { useMemo } from 'react';
import { sanitize } from 'helpers/utils/htmlSanitize';
import Button from 'components/commercetools-ui/atoms/button';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import Image from 'frontastic/lib/image';
import Slider from 'components/commercetools-ui/atoms/slider';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useWindowWidth from 'helpers/hooks/useBrowerWidth';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import {
  desktop,
  largeDesktop,
  tablet,
  extraLargeDesktop,
} from 'helpers/utils/screensizes';

import { GenericSectionContent, MediaCard, Cta } from '../../types';
import { useAdditionalCss } from '../../../helpers/useAdditionalCss';
import styles from './index.module.css';

export default function LyraRendering({ content }: { content: GenericSectionContent }) {
  const { cards, additional_css, additional_css_class_names } = content;

  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'LyraRendering');

  const { isTouchDevice } = useTouchDevice();
  const [isHugeDesktop2Size] = useMediaQuery(extraLargeDesktop);
  const [isLargeDesktopSize] = useMediaQuery(largeDesktop);
  const [isDesktopSize] = useMediaQuery(desktop);
  const [isTabletSize] = useMediaQuery(tablet);
  const width = useWindowWidth();

  const showArrows = useMemo(
    () =>
      !isTouchDevice &&
      !(
        (isLargeDesktopSize && cards?.length <= 4) ||
        (!isLargeDesktopSize && cards?.length <= 2)
      ),
    [isLargeDesktopSize, isTouchDevice, cards?.length],
  );

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      <div className={`image-content w-full px-20 pt-24 pb-24 lg:pt-40 lg:pb-40 2xl:pt-60 2xl:pb-60 ${[additional_css_class_names, uniqueClass].filter(Boolean).join(' ')}`}>
        <Slider
          containerClassName={showArrows ? 'px-48' : ''}
          slidesPerView={1}
          dots={width !== null && width < 1023}
          arrows={isDesktopSize}
          allowArrowsOnTouchDevice
          innerArrows
          allowTouchMove={true}
          // Lyra used to autoplay at Swiper's own 3s default — it passed no `autoplay` at all and
          // the Slider's init effect starts autoplay regardless. 3s is too fast for a slide with a
          // heading, a paragraph and a CTA to be read, so it now opts in explicitly at 9s.
          autoplay
          autoplayDelay={8000}
          prevButtonStyles={{
            top: '90%',
            left: isHugeDesktop2Size ? '59%' : isDesktopSize ? '65%' : width !== null && width == 1024 ? 650 : 500,
            padding: '20px',
          }}
          nextButtonStyles={{
            top: '90%',
            left: isHugeDesktop2Size ? '62%' : isDesktopSize ? '70%' : width !== null && width == 1024 ? 650 : 500,
            padding: '20px',
          }}
          className='bg-[#E5E9EB]'
        >
          {cards?.map(
            (slide: MediaCard, index: number) => (
              <div key={index} className={width !== null && width > 1023 ? 'flex' : 'flex flex-col'}>
                <div className="w-60%">
                  {slide?.desktop_image?.url ? (
                    <Image
                      loading='lazy'
                      src={slide?.desktop_image?.url}
                      alt={slide?.desktop_image?.description ?? ''}
                      width={isTabletSize ? 1300 : 750}
                      className="brightness-75"
                    />
                  ) : (
                    <div className="flex-1"></div>
                  )}
                </div>
                <div className="">
                  <div className="w-40% flex-1">
                    {isTabletSize ? (
                      <div
                        className={`pl-20 pt-48 md:px-40 lg:pb-50 lg:pl-48 ${
                          width !== null && width < 1023 ? 'pb-50' : ''
                        } xl:pb-100 2xl:pb-232`}
                      >
                        <div className="md:w-[90%] xl:w-[65%] 2xl:w-[55%] ">
                          <Typography
                            as="h1"
                            className="text-left text-24 font-medium leading-[3.25rem] text-[#090909] xl:text-36 2xl:text-48"
                          >
                            {slide?.title}
                          </Typography>
                        </div>
                        <div className="xl:w-[78%] 2xl:w-[65%] ">
                          <div
                            className="mt-22 text-left text-16 font-normal leading-8 text-[#333] 2xl:text-20"
                            dangerouslySetInnerHTML={{ __html: sanitize(slide?.description ?? '') }}
                          />
                        </div>
                        {slide?.ctas &&
                          slide?.ctas.map((cta: Cta) => (
                            <Link link={cta?.url} key={cta?.label} target={cta?.target}>
                              <Button
                                className="mt-48 rounded-none border border-black bg-black px-32 py-18 shadow-sm hover:!bg-[#394A54]"
                              >
                                <Typography as="span" className="text-16 font-medium leading-6 text-white">
                                  {cta?.label}
                                </Typography>
                              </Button>
                            </Link>
                          ))}
                      </div>
                    ) : (
                      <div className="flex-1 bg-[#E5E9EB] px-24 py-24 pb-74">
                        <Typography as="h1" className="text-left text-24 font-medium leading-8 text-[#090909]">
                          {slide?.title}
                        </Typography>
                        <div
                          className="mt-16 text-left text-16 font-normal leading-6 text-[#090909]"
                          dangerouslySetInnerHTML={{ __html: sanitize(slide?.description ?? '') }}
                        />
                        {slide?.ctas &&
                          slide?.ctas.map((cta: Cta) => (
                            <Link link={cta?.url} key={cta?.label} target={cta?.target}>
                              <Button
                                className="mt-24 w-full rounded-none border border-black bg-black py-18 shadow-sm hover:!bg-[#394A54]"
                              >
                                <Typography as="span" className="text-16 font-medium leading-6 text-white">
                                  {cta?.label}
                                </Typography>
                              </Button>
                            </Link>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ),
          )}
        </Slider>
      </div>
    </>
  );
}
